import React, { useState } from 'react';
import { HelpCircle, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

interface HelpPanelProps {
  title: string;
  storageKey: string;
  children: React.ReactNode;
}

// Collapsible help panel that persists its dismissed state to localStorage.
// Sits below the page header to provide contextual guidance.

export default function HelpPanel({ title, storageKey, children }: HelpPanelProps) {
  const lsKey = `cadence_help_dismissed_${storageKey}`;
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(lsKey) === 'true');
  const [expanded, setExpanded] = useState(false);

  if (dismissed) return null;

  return (
    <Card className="mb-3 border-primary/20 bg-primary/[0.04]">
      <CardContent className="px-3 py-2">
        <div className="flex items-center justify-between gap-1">
          <button
            type="button"
            className="flex flex-1 items-center gap-2 text-left"
            onClick={() => setExpanded(!expanded)}
          >
            <HelpCircle className="h-5 w-5 text-primary" />
            <span className="text-sm font-semibold text-primary">{title}</span>
          </button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            aria-label="Dismiss help"
            onClick={() => {
              setDismissed(true);
              localStorage.setItem(lsKey, 'true');
            }}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        {expanded && (
          <p className="mt-2 text-sm text-muted-foreground">{children}</p>
        )}
      </CardContent>
    </Card>
  );
}
